import { Copy01 } from "@untitledui/icons";
import { type ReactElement, type ReactNode, useEffect, useRef } from "react";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { useClipboard } from "@/hooks/use-clipboard";
import { cx } from "@/utils/cx";
import { DisplayFieldShell } from "./DisplayFieldShell";
import {
	type DisplayFieldBaseProps,
	DisplayFieldContent,
	DisplayFieldFeedback,
	DisplayFieldState,
	displayFieldTrailingIconToneClass,
	resolveDisplayContent,
} from "./display-field.shared";

export interface DisplayInputProps extends DisplayFieldBaseProps {
	/** Displayed value when populated. */
	value?: ReactNode;
	/**
	 * Show copy control (typically Enabled + Populated, e.g. IDs).
	 * @default false
	 */
	copyable?: boolean;
	/** Text copied to clipboard. Defaults to stringified `value`. */
	copyText?: string;
	/** Called when copy is activated. */
	onCopy?: () => void;
	/** Accessible name for the copy control. */
	copyAriaLabel?: string;
}

/**
 * Compact underline field for label + value in drawers / detail panels.
 * When `state` is Active, the value row becomes an inline text input.
 * Pass `feedback` for auto-save status: Saving (spinner) → Saved (check) → End (fade).
 *
 * @see Figma DisplayInput 3353:25434
 * @see Figma FeedbackIndicator Saving 5228:89418 / Saved 5228:89757
 */
export function DisplayInput({
	label,
	value,
	state = DisplayFieldState.Enabled,
	content: contentProp,
	leadingIcon,
	showLeadingIcon,
	hintText,
	showHintText,
	feedback = DisplayFieldFeedback.Idle,
	copyable = false,
	copyText,
	onCopy,
	copyAriaLabel,
	showEdit,
	onEdit,
	editAriaLabel,
	editValue,
	onEditChange,
	onEditCommit,
	onEditCancel,
	className,
	"data-testid": dataTestId = "display-input",
}: DisplayInputProps): ReactElement {
	const content = resolveDisplayContent(contentProp, value);
	const isPopulated = content === DisplayFieldContent.Populated;
	const isDisabled = state === DisplayFieldState.Disabled;
	const isActive = state === DisplayFieldState.Active;
	const isHovered = state === DisplayFieldState.Hovered;
	const showCopy =
		copyable &&
		isPopulated &&
		!isActive &&
		feedback === DisplayFieldFeedback.Idle;
	const trailingIconTone = displayFieldTrailingIconToneClass({
		isDisabled,
		isEmphasized: isHovered || isActive,
	});
	const inputRef = useRef<HTMLInputElement>(null);
	const { copy, copied } = useClipboard();
	const showCopied = copied === "display-input";

	const resolvedCopyText =
		copyText ??
		(typeof value === "string" || typeof value === "number"
			? String(value)
			: "");

	const draft =
		editValue ??
		(typeof value === "string" || typeof value === "number"
			? String(value)
			: "");

	useEffect(() => {
		if (!isActive) return;
		const el = inputRef.current;
		if (!el) return;
		el.focus();
		const len = el.value.length;
		el.setSelectionRange(len, len);
	}, [isActive]);

	return (
		<DisplayFieldShell
			label={label}
			state={state}
			isPopulated={isPopulated || isActive}
			leadingIcon={leadingIcon}
			showLeadingIcon={showLeadingIcon}
			hintText={hintText}
			showHintText={showHintText}
			feedback={feedback}
			showEdit={(showEdit ?? true) && !showCopy && !isDisabled}
			onEdit={onEdit}
			editAriaLabel={editAriaLabel}
			onClear={
				isActive
					? () => {
							onEditChange?.("");
						}
					: undefined
			}
			clearAriaLabel={`Clear ${label}`}
			className={className}
			data-testid={dataTestId}
			trailing={
				showCopy ? (
					<Tooltip
						title="Copied!"
						placement="top"
						isOpen={showCopied}
						isDisabled={!showCopied}
					>
						<TooltipTrigger
							aria-label={
								showCopied ? "Copied!" : (copyAriaLabel ?? `Copy ${label}`)
							}
							className={cx(
								"flex size-[21px] cursor-pointer items-center justify-center rounded-md border outline-focus-ring transition-all duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed",
								showCopied
									? "border-success-800 text-success-800"
									: cx("border-transparent", trailingIconTone),
							)}
							isDisabled={isDisabled}
							onPress={() => {
								if (resolvedCopyText) {
									void copy(resolvedCopyText, "display-input");
								}
								onCopy?.();
							}}
						>
							<Copy01
								className={cx(
									"size-5 transition-colors duration-200",
									showCopied && "text-success-800",
								)}
							/>
						</TooltipTrigger>
					</Tooltip>
				) : undefined
			}
		>
			{isActive ? (
				<input
					ref={inputRef}
					type="text"
					aria-label={label}
					value={draft}
					onChange={(event) => onEditChange?.(event.target.value)}
					onBlur={() => onEditCommit?.()}
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							event.preventDefault();
							(event.target as HTMLInputElement).blur();
						}
						if (event.key === "Escape") {
							event.preventDefault();
							event.stopPropagation();
							onEditCancel?.();
						}
					}}
					className="w-full min-w-0 cursor-text border-0 bg-transparent p-0 font-display text-sm font-extrabold text-primary outline-none placeholder:font-semibold placeholder:text-quaternary"
					placeholder={label}
				/>
			) : null}
			{!isActive && isPopulated ? (
				<p className="w-full truncate font-display text-sm font-extrabold text-primary">
					{value}
				</p>
			) : null}
		</DisplayFieldShell>
	);
}
